import { Component, PLATFORM_ID, Inject, OnInit } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
	SupabaseService
} from '@app/core/services';
// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// ** rxjs
import { finalize, map, switchMap, tap, catchError } from 'rxjs/operators';
import { of, Observable, from, EMPTY } from 'rxjs';
import * as moment from 'moment';
import { MessageService } from '@app/core/services';

// 本元件為 human-recruit.component 的動態版本：
// 不再把 tableName / 商品圖片 / 標題 / 說明文字寫死在程式碼中，
// 而是依網址參數 productId (如 'BAS-048') 向 Supabase 的 GroupBreaks 表查詢商品 metadata 後再渲染。
@Component({
	selector: 'app-group-break-detail',
	templateUrl: './group-break-detail.component.html',
	styleUrls: ['./group-break-detail.component.scss']
})
export class GroupBreakDetailComponent implements OnInit {
	nbaMap = new Map();
	isBrowser: boolean;

	mode = 'list'; // list || detail || success

	// 商品 metadata (title / description / image / tableName ...)，動態從 Supabase GroupBreaks 表取得
	productId: string;
	groupBreak: any = null;

	// 所有隊伍 - (isLock = null or false)
	teamsList = [];
	// 被選中的隊伍
	selectedTeamsList = [];
	colsTeams = [
		{ field: 'checkbox', header: '', width: '1rem' },
        { field: 'name', header: '編號', width: '12rem' },
		{ field: 'priceNum', header: '價格', width: '12rem' }
    ];

	buyerForm: FormGroup = this.formBuilder.group({
		buyerName: ['', Validators.required],
		buyerNickName: [''],
		buyerEmail: ['', [Validators.required, Validators.email]],
		buyerPhone: ['', Validators.required],
		buyerBankAccount: ['', Validators.required],
		'711Name': ['', Validators.required],
		submitTaiwanTime: [''],
		isLock: [null],
		groupIdForSentEmail: [''],
		discountCode: [''],
		discountAmount: [null],
		needEmptyCard: [null],
		addDisplayCard: [0]
	});

	boolOptions = [
		{
			"value": true,
			"label": "是"
		},
		{
			"value": false,
			"label": "否"
		}
	]

	isDiscountCode = false;
	discountCodeOptions = [];

	constructor(
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) private platformId,
		private route: ActivatedRoute,
		private router: Router,
		private supabaseService: SupabaseService,
		private formBuilder: FormBuilder,
		public layoutService: LayoutService,
		public messageService: MessageService
	) {
		this.isBrowser = isPlatformBrowser(this.platformId);
	}

	ngOnInit(): void {
		this.productId = this.route.snapshot.paramMap.get('productId');

		of(this.layoutService.setPageLoading(true))
			.pipe(
				switchMap(() => this.getGroupBreak$()),
				switchMap(() => this.getTeamsList()),
				switchMap(() => this.getSession$()),
				tap((res) => this.setNBALogoMap()),
				tap((sessionRes) => this.setUserData(sessionRes)),
				switchMap((sessionRes) => {
					// 判斷是否有登入 (SSR/prerender 環境沒有 localStorage，一併排除)
					if (!sessionRes.session || !this.isBrowser) return of(null);
					else return this.getDiscountCode$(JSON.parse(localStorage.getItem('profile')).id)
				}),
				tap((discountCodeRes) => {
					// 如有登入才去往下執行(沒有折扣碼也會執行，但不影響前端顯示 - 即為空下拉選單，不會壞掉)
					if (!!discountCodeRes) {
						this.isDiscountCode = true;
						this.discountCodeOptions = discountCodeRes.map((item) => {
							return {
								label: item.discountCode + ' - $' + item.discountAmount,
								value: item.discountCode
							}
						})
					}
				}),
				catchError((err) => {
					console.error(err);
					this.router.navigate(['/page-not-found']);
					return EMPTY;
				}),
				finalize(() => this.layoutService.setPageLoading(false))
			)
			.subscribe();
	}

	// - 依 productId 取得商品 metadata（找不到就丟出錯誤，交由外層導向 page-not-found）
	getGroupBreak$(): Observable<any> {
		return from(this.supabaseService.getGroupBreakByProductId(this.productId)).pipe(
			map(({ data, error }) => {
				if (error || !data) throw new Error('GROUP_BREAK_NOT_FOUND');
				this.groupBreak = data;
				const canonical = this.document.querySelector('link[rel="canonical"]') as HTMLAnchorElement | null;
				if (canonical) canonical.href = `https://bigaunt666.github.io/bigaunt/${this.productId}`;
				return data;
			})
		);
	}

	// 取得NBA官網logo並製成map
	setNBALogoMap() {
		this.nbaMap.set('湖人隊', 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg');
		this.nbaMap.set('馬刺隊', 'https://cdn.nba.com/logos/nba/1610612759/global/L/logo.svg');
		this.nbaMap.set('勇士隊', 'https://cdn.nba.com/logos/nba/1610612744/global/L/logo.svg');
		this.nbaMap.set('雷霆隊', 'https://cdn.nba.com/logos/nba/1610612760/global/L/logo.svg');
		this.nbaMap.set('獨行俠隊', 'https://cdn.nba.com/logos/nba/1610612742/global/L/logo.svg');
		this.nbaMap.set('賽爾提克隊', 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg');
		this.nbaMap.set('灰狼隊', 'https://cdn.nba.com/logos/nba/1610612750/global/L/logo.svg');
		this.nbaMap.set('公牛隊', 'https://cdn.nba.com/logos/nba/1610612741/global/L/logo.svg');
		this.nbaMap.set('76人隊', 'https://cdn.nba.com/logos/nba/1610612755/global/L/logo.svg');
		this.nbaMap.set('老鷹隊', 'https://cdn.nba.com/logos/nba/1610612737/global/L/logo.svg');
		this.nbaMap.set('火箭隊', 'https://cdn.nba.com/logos/nba/1610612745/global/L/logo.svg');
		this.nbaMap.set('灰熊隊', 'https://cdn.nba.com/logos/nba/1610612763/global/L/logo.svg');
		this.nbaMap.set('巫師隊', 'https://cdn.nba.com/logos/nba/1610612764/global/L/logo.svg');
		this.nbaMap.set('拓荒者隊', 'https://cdn.nba.com/logos/nba/1610612757/global/L/logo.svg');
		this.nbaMap.set('金塊隊', 'https://cdn.nba.com/logos/nba/1610612743/global/L/logo.svg');
		this.nbaMap.set('活塞隊', 'https://cdn.nba.com/logos/nba/1610612765/global/L/logo.svg');
		this.nbaMap.set('尼克隊', 'https://cdn.nba.com/logos/nba/1610612752/global/L/logo.svg');
		this.nbaMap.set('溜馬隊', 'https://cdn.nba.com/logos/nba/1610612754/global/L/logo.svg');
		this.nbaMap.set('公鹿隊', 'https://cdn.nba.com/logos/nba/1610612749/global/L/logo.svg');
		this.nbaMap.set('熱火隊', 'https://cdn.nba.com/logos/nba/1610612748/global/L/logo.svg');
		this.nbaMap.set('太陽隊', 'https://cdn.nba.com/logos/nba/1610612756/global/L/logo.svg');
		this.nbaMap.set('魔術隊', 'https://cdn.nba.com/logos/nba/1610612753/global/L/logo.svg');
		this.nbaMap.set('爵士隊', 'https://cdn.nba.com/logos/nba/1610612762/global/L/logo.svg');
		this.nbaMap.set('暴龍隊', 'https://cdn.nba.com/logos/nba/1610612761/global/L/logo.svg');
		this.nbaMap.set('騎士隊', 'https://cdn.nba.com/logos/nba/1610612739/global/L/logo.svg');
		this.nbaMap.set('黃蜂隊', 'https://cdn.nba.com/logos/nba/1610612766/global/L/logo.svg');
		this.nbaMap.set('鵜鶘隊', 'https://cdn.nba.com/logos/nba/1610612740/global/L/logo.svg');
		this.nbaMap.set('國王隊', 'https://cdn.nba.com/logos/nba/1610612758/global/L/logo.svg');
		this.nbaMap.set('快艇隊', 'https://cdn.nba.com/logos/nba/1610612746/global/L/logo.svg');
		this.nbaMap.set('籃網隊', 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg');
	}

	// 帶入會員資料
	setUserData(sessionRes) {
		if (!sessionRes.session || !this.isBrowser) return;
		let profile = JSON.parse(localStorage.getItem('profile'));
		const userData = {
			buyerName: profile.name,
			buyerNickName: profile.nickName,
			buyerEmail: profile.email,
			buyerPhone: profile.phone,
			buyerBankAccount: profile.bankAccount,
			'711Name': profile['711Name']
		};
		this.buyerForm.patchValue(userData);
	}

	// 點擊選取隊伍
	clickSelectTeams(item) {
		item.isSelected = !item.isSelected;
	}

	getTeamsList(): Observable<any> {
		return from(this.supabaseService.getTeams(this.groupBreak.tableName)).pipe(
			tap(({ data }) => {
				this.teamsList = data;
			})
		);
	}

	openDetailPage() {
		// 重置被選中隊伍
		this.selectedTeamsList = [];
		// 重置折扣金額為0
		this.buyerForm.controls.discountAmount.setValue(0);

		// 檢查必填欄位及格式
		if (this.buyerForm.invalid) {
			this.messageService.add({
				severity: 'error',
				summary: `注意`,
				detail: '尚有必填欄位未填寫或是格式不對',
				icon: 'bounce animated',
				life: 5000,
			});
			return;
		}
		let selectedTeamsIdArr = [];
		const selectedTeamsList = this.teamsList.filter((item) => {
			// 將被選取的隊伍整理成id陣列，送去checkThisTeamIsLock做檢查
			if (!!item.isSelected) selectedTeamsIdArr.push(item.id)
			return !!item.isSelected
		});
		this.selectedTeamsList = selectedTeamsList;
		if (selectedTeamsIdArr.length <= 0) {
			this.messageService.add({
				severity: 'error',
				summary: `注意`,
				detail: '尚未選擇任何隊伍',
				icon: 'bounce animated',
				life: 5000,
			});
			return;
		}
		if (this.buyerForm.value.addDisplayCard < 0) {
			this.messageService.add({
				severity: 'error',
				summary: `注意`,
				detail: '球卡支架數量不得為負數',
				icon: 'bounce animated',
				life: 5000,
			});
			return;
		}
		if (!!this.buyerForm.value.discountCode) {
			of(null)
				.pipe(
					tap(() => this.layoutService.setPageLoading(true)),
					switchMap(() => this.checkDiscountCode$(this.buyerForm.value.discountCode)),
					switchMap((discountInfo) => {
						if (discountInfo.length <= 0) {
							this.messageService.add({
								severity: 'error',
								summary: `注意`,
								detail: '折扣代碼不存在',
								icon: 'bounce animated',
								life: 5000,
							});
							return EMPTY;
						} else {
							this.buyerForm.controls.discountAmount.setValue(discountInfo[0].discountAmount);
							return of(true);
						}
					}),
					finalize(() => this.layoutService.setPageLoading(false))
				)
				.subscribe(() => {
					this.mode = 'detail';
				});
		} else this.mode = 'detail';
	}

	// 計算所有隊伍費用總額
	calcTotalTeamsCost(teams) {
		let total = teams.reduce((total, item) => {
			const numericPrice = item.priceNum;
			return total + (isNaN(numericPrice) ? 0 : numericPrice); // 保險起見，排除非數字
		}, 0);
		total = !!this.buyerForm.value.addDisplayCard ? total + this.buyerForm.value.addDisplayCard * 10 : total;
		let discountAmount = this.buyerForm.value.discountAmount;
		let result;
		if (!!discountAmount) {
			if (teams.length >= 2) result = Math.round(total * 0.9) - discountAmount;
			else result = total - discountAmount;
		} else {
			if (teams.length >= 2) result = Math.round(total * 0.9);
			else result = total;
		}
		result = result <= 0 ? 0 : result;
		return result;
	}

	generateUUIDv4() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
			const r = Math.random() * 16 | 0;
			const v = c === 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	// 按下確定訂單
	confirmOrderDetail() {
		of(null)
			.pipe(
				tap(() => this.layoutService.setPageLoading(true)),
				// 1. 設定基本欄位
				tap(() => {
					this.buyerForm.patchValue({
						submitTaiwanTime: moment().format('YYYY-MM-DD HH:mm:ss'),
						isLock: true,
						groupIdForSentEmail: this.generateUUIDv4(),
					});
				}),
				// 2. 取得選中的隊伍 id
				map(() =>
					this.teamsList
						.filter(
							t => t.isSelected
						)
						.map(t => t.id)
				),
				// 3. 直接搶隊（取代 check + update）
				switchMap(
					selectedTeamsIdArr =>
						this.lockTeams$(selectedTeamsIdArr).pipe(
							map(success => ({ success, selectedTeamsIdArr}))
						)
				),

				// 4. 搶失敗
				tap(({ success }) => {
					if (!success) {
						this.messageService.add(
							{
								severity: 'error',
								summary: '注意',
								detail: '您選中的隊伍已被別人搶先一步，請重新整理頁面後再選取',
								icon: 'bounce animated',
								life: 5000,
							}
						);
						throw new Error( 'TEAM_LOCKED');
					}
				}),
				// 5. 更新折扣碼
				switchMap(() => {
					return this
						.buyerForm.value
						.discountCode !==
						'GUTURTLE100'
						? this.updateDiscountCodeToIsUsed$(
								this
									.buyerForm
									.value
									.discountCode
						)
						: of(null);
				}),
				finalize(() => this.layoutService.setPageLoading(false))
			)
			.subscribe({
				next: () => {
					console.log('完成表單資料：', this.buyerForm.getRawValue());
				},
				error: err => {
					if (err.message !== 'TEAM_LOCKED') {
						console.error(err);
						this.messageService.add(
							{
								severity: 'error',
								summary: '系統錯誤',
								detail: '訂單建立失敗，請稍後再試',
								life: 5000,
							}
						);
					}
				},
			});
	}

	// - 確認是否有任一隊伍是否已被其他人選取
	checkAnyTeamsIsLock$(selectedTeamsIdArr: string[]): Observable<boolean> {
		return from(this.supabaseService.getTeamsById(selectedTeamsIdArr, this.groupBreak.tableName)).pipe(
			map(({ data }) => {
				if (!data) return false;
				return data.some(item => !!item.isLock);
			})
		);
	}


	// - 送出表單
	updateTeamInfo$(selectedTeamsIdArr: string[]) {
		return from(
			this.supabaseService.updateTeamInfo(
				selectedTeamsIdArr,
				this.buyerForm.value,
				this.groupBreak.tableName
			)
		).pipe(
			tap(({ data }) => {
				if (!!data) {
					this.messageService.add({
						severity: 'success',
						summary: `成功`,
						detail: '訂購成功',
						icon: 'bounce animated',
						life: 5000,
					});
					this.mode = 'success';
			}
			}),
			catchError(err => {
				console.error('Unexpected error:', err);
				this.messageService.add({
					severity: 'error',
					summary: `錯誤`,
					detail: '請聯絡大阿姨團隊',
					icon: 'bounce animated',
					life: 5000,
				});
				return of(null); // Observable 必須回傳可訂閱的東西
			})
		);
	}

	// - 檢查折扣代碼是否存在
	checkDiscountCode$(discountCode): Observable<any> {
		return from(this.supabaseService.checkDiscountCode(discountCode)).pipe(
			map(({ data }) => data),
			catchError((err) => {
				console.error('Unexpected error:', err);
				return of(false); // 回傳 false 代表錯誤
			})
		);
	}

	// - 將使用過折扣代碼isUsed更新為true
	updateDiscountCodeToIsUsed$(discountCode: string): Observable<any> {
		return from(this.supabaseService.updateDiscountCodeToIsUsed(discountCode)).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			}),
			catchError((err) => {
				console.error('Unexpected error:', err);
				return of(false); // 回傳 false 表示失敗
			})
		);
	}

	// - 取得登入狀態
	getSession$(): Observable<any> {
		return from(this.supabaseService.getSession()).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}

	// - 取得個人折扣代碼
	getDiscountCode$(personId: any): Observable<any> {
		return from(this.supabaseService.getDiscountCode(personId)).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}

	// - 搶隊伍 + 寫入購買資料
	lockTeams$(selectedTeamIds: number[]): Observable<boolean> {
		return from(
			this.supabaseService.lockTeams(
				this.groupBreak.tableName,
				selectedTeamIds,
				this.buyerForm.getRawValue()
			)
		).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data as boolean;
			}),

			tap(success => {
				if (success) {
					this.messageService.add({
						severity: 'success',
						summary: '成功',
						detail: '訂購成功',
						icon: 'bounce animated',
						life: 5000,
					});

					this.mode = 'success';
				}
			}),

			catchError(err => {
				console.error('Unexpected error:', err);

				this.messageService.add({
					severity: 'error',
					summary: '錯誤',
					detail: '請聯絡大阿姨團隊',
					icon: 'bounce animated',
					life: 5000,
				});

				return of(false); // 保持 Observable 流不中斷
			})
		);
	}
}
